import { Lock, Send } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";

import { EmptyState } from "../components/EmptyState";
import { useActiveModules } from "../lib/active-modules";
import { valoreDelCampo } from "../lib/field-value";
import { trpc, type UserSession } from "../lib/trpc";

function orario(data: Date | string) {
  return new Date(data).toLocaleString("it-IT", {
    day: "numeric",
    month: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * La chat interna all'azienda.
 *
 * Chi lavora ha una conversazione sola e ci entra dentro subito, senza scegliere niente: sul
 * telefono, in cantiere, ogni schermata in mezzo e un ostacolo. Il titolare con piu persone
 * sceglie prima con chi parlare. Nessuna delle due parti puo uscire dall'azienda, e nessuno
 * da fuori puo entrare: la porta e chiusa sul server, non qui.
 */
export function ChatPage({ session }: { session: UserSession }) {
  const utils = trpc.useUtils();
  const { attivi } = useActiveModules();
  const titolare = session.membership.role === "owner";
  // Durante un accesso di assistenza il server chiude la chat, e fa bene: i messaggi fra il
  // titolare e la squadra restano privati anche per chi mantiene il programma. Qui non si
  // chiede niente, perche un rifiuto atteso non e un errore da incassare ogni venti secondi,
  // e soprattutto perche il motivo va detto per nome: prima questa schermata dava la colpa
  // al modulo spento anche quando l'interruttore in console era acceso.
  // Due motivi diversi per la stessa porta chiusa, e vanno detti per nome. Se il modulo e
  // spento vince quello, perche e il motivo piu di fondo: dire "il modulo e acceso ma
  // l'assistenza lo chiude" a chi il modulo non ce l'ha sarebbe una bugia al contrario.
  const moduloAcceso = attivi.has("communications");
  const assistenza = Boolean(session.impersonation) && moduloAcceso;
  // Il rifiuto qui vuol dire allora una cosa sola: il modulo non e acceso per questa
  // azienda, e qualcuno e arrivato scrivendo l'indirizzo a mano. Va detto, perche il
  // messaggio della squadra vuota racconterebbe un motivo sbagliato.
  const canali = trpc.chat.canali.useQuery(undefined, {
    enabled: !assistenza,
    refetchInterval: 20_000,
    retry: false,
  });
  const [sceltoId, setSceltoId] = useState<string | null>(null);

  const elenco = canali.data ?? [];
  const aperto = titolare
    ? (sceltoId ?? (elenco.length === 1 ? elenco[0].memberUserId : null))
    : (elenco[0]?.memberUserId ?? null);

  const messaggi = trpc.chat.messaggi.useQuery(
    { memberUserId: titolare ? (aperto ?? undefined) : undefined },
    { enabled: !assistenza && (Boolean(aperto) || !titolare), refetchInterval: 15_000 },
  );
  const invia = trpc.chat.invia.useMutation();
  const segnaLetto = trpc.chat.segnaLetto.useMutation({ onSuccess: () => void utils.chat.invalidate() });
  const [testo, setTesto] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const campo = useRef<HTMLInputElement>(null);
  const fondo = useRef<HTMLDivElement>(null);

  // Aperta la conversazione, quello che c'era dentro e stato visto: si segna letto una volta
  // per apertura, non a ogni respiro della pagina.
  const canaleLetto = useRef<string | null>(null);
  useEffect(() => {
    const chiave = aperto ?? (titolare ? null : session.user.id);
    if (!chiave || !messaggi.isSuccess || canaleLetto.current === chiave) return;
    const daLeggere = (messaggi.data ?? []).some((messaggio) => !messaggio.mio && !messaggio.readAt);
    canaleLetto.current = chiave;
    if (daLeggere) segnaLetto.mutate(titolare ? { memberUserId: chiave } : {});
  }, [aperto, titolare, messaggi.data, messaggi.isSuccess, segnaLetto, session.user.id]);

  useEffect(() => {
    fondo.current?.scrollIntoView({ block: "end" });
  }, [messaggi.data]);

  async function manda(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Si crede al campo, non allo stato: con la dettatura il testo c'e ma React non lo sa.
    const scritto = valoreDelCampo(campo.current, testo);
    if (!scritto) {
      setErrore("Scrivi qualcosa prima di inviare.");
      return;
    }
    setErrore(null);
    try {
      await invia.mutateAsync({ memberUserId: titolare ? (aperto ?? undefined) : undefined, body: scritto });
      setTesto("");
      if (campo.current) campo.current.value = "";
      void utils.chat.invalidate();
    } catch (problema) {
      setErrore(problema instanceof Error ? problema.message : "Il messaggio non e partito. Riprova.");
    }
  }

  return (
    <main className="page chat-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">Solo dentro l'azienda</p>
          <h1>Comunicazioni</h1>
          <p>{titolare ? "Scrivi alle persone della tua squadra." : "Scrivi al titolare."}</p>
        </div>
      </header>

      {assistenza && (
        <EmptyState title="La chat resta chiusa durante l'accesso di assistenza">
          Il modulo Comunicazioni e acceso: quello che non si apre e la conversazione. I messaggi
          fra il titolare e la sua squadra restano privati anche per chi mantiene il programma, e
          un accesso di assistenza non li mostra mai. Per provare la chat davvero, esci
          dall'assistenza ed entra con le credenziali di una persona dell'azienda.
        </EmptyState>
      )}

      {!assistenza && moduloAcceso && titolare && elenco.length > 1 && (
        <div className="chat-canali" role="tablist" aria-label="Conversazioni">
          {elenco.map((canale) => (
            <button
              key={canale.memberUserId}
              type="button"
              role="tab"
              aria-selected={canale.memberUserId === aperto}
              className={`chat-canali__voce${canale.memberUserId === aperto ? " chat-canali__voce--attiva" : ""}`}
              onClick={() => {
                setSceltoId(canale.memberUserId);
                canaleLetto.current = null;
              }}
            >
              {canale.memberName}
              {canale.nonLetti > 0 && <span className="chat-canali__pallino">{canale.nonLetti}</span>}
            </button>
          ))}
        </div>
      )}

      {!assistenza && (canali.isError || !moduloAcceso) && (
        <EmptyState title="Le Comunicazioni non sono attive">
          Questa sezione fa parte del modulo Comunicazioni, che per la vostra azienda non e acceso.
          Se vi serve, scrivetecelo dal Supporto.
        </EmptyState>
      )}

      {!assistenza && moduloAcceso && !canali.isError && titolare && elenco.length === 0 && (
        <p className="chat-vuota">Quando aggiungi qualcuno alla squadra, la conversazione compare qui.</p>
      )}

      {!assistenza && moduloAcceso && !canali.isError && (aperto || !titolare) && (
        <section className="chat">
          <div className="chat__messaggi">
            {(messaggi.data ?? []).length === 0 && (
              <p className="chat-vuota">Ancora nessun messaggio. Comincia tu.</p>
            )}
            {(messaggi.data ?? []).map((messaggio) => (
              <div key={messaggio.id} className={`bolla${messaggio.mio ? " bolla--mia" : ""}`}>
                <p className="bolla__testo">{messaggio.body}</p>
                <p className="bolla__firma">
                  {messaggio.mio ? "tu" : messaggio.autore.name} · {orario(messaggio.createdAt)}
                </p>
              </div>
            ))}
            <div ref={fondo} />
          </div>

          <form className="chat__scrivi" onSubmit={manda}>
            <input
              ref={campo}
              value={testo}
              onChange={(event) => {
                setTesto(event.target.value);
                setErrore(null);
              }}
              placeholder="Scrivi un messaggio..."
              maxLength={2000}
              aria-label="Scrivi un messaggio"
            />
            {/* Sempre acceso: si controlla al momento dell'invio e si dice cosa manca. */}
            <button className={`button button--primary${invia.isPending ? " button--saving" : ""}`} disabled={invia.isPending}>
              <Send size={17} aria-hidden="true" /> Invia
            </button>
          </form>
          {errore && <p className="form-error" role="alert">{errore}</p>}
        </section>
      )}

      {!assistenza && moduloAcceso && !canali.isError && (
      <p className="chat-promessa">
        <Lock size={15} aria-hidden="true" /> Questi messaggi restano dentro l'azienda. Non li vede nessun'altra
        azienda e non li legge chi ha costruito il programma.
      </p>
      )}
    </main>
  );
}
